import { useEffect, useState, useRef } from "react";
import { formatNumberWithSeparators } from "../utilities";
import LoadingTable from "./LoadingTable";
import { InformationCircleIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useNavigate } from "react-router-dom";

export default function FortnoxTable({ fortnoxList = { resources: [], total: 0 }, isLoading }) {
  const [addedRows, setAddedRows] = useState(new Set());
  const [ignoredRows, setIgnoredRows] = useState(new Set());
  const [openMessageId, setOpenMessageId] = useState(null);
  const popupRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const savedAddedRows = localStorage.getItem("addedRows");
      const savedIgnoredRows = localStorage.getItem("ignoredRows");
      
      setAddedRows(new Set(savedAddedRows ? JSON.parse(savedAddedRows) : []));
      setIgnoredRows(new Set(savedIgnoredRows ? JSON.parse(savedIgnoredRows) : []));
    } catch (error) {
      setAddedRows(new Set());
      setIgnoredRows(new Set());
    }
  }, []);

  useEffect(() => {
    try {
      if (addedRows.size > 0) {
        localStorage.setItem("addedRows", JSON.stringify([...addedRows]));
      } else {
        localStorage.removeItem("addedRows");
      }
      
      if (ignoredRows.size > 0) {
        localStorage.setItem("ignoredRows", JSON.stringify([...ignoredRows]));
      } else {
        localStorage.removeItem("ignoredRows");
      }
    } catch (error) {
    }
  }, [addedRows, ignoredRows]);

  const handleAdd = (e, id) => {
    e.stopPropagation(); // Prevent row click navigation
    if (addedRows.has(id)) {
      setAddedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } else {
      setAddedRows((prev) => new Set(prev).add(id));
      setIgnoredRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleIgnore = (e, id) => {
    e.stopPropagation(); // Prevent row click navigation
    if (ignoredRows.has(id)) {
      setIgnoredRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } else {
      setIgnoredRows((prev) => new Set(prev).add(id));
      setAddedRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleOutsideClick = (e) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      setOpenMessageId(null);
    }
  };

  const handleRowClick = (vizionResourceId, status) => {
    if (status === 2) {
      navigate(`/invoice/${vizionResourceId}`);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  if (isLoading) {
    return <LoadingTable size={10}></LoadingTable>;
  }

  if (!fortnoxList || !fortnoxList.resources || fortnoxList.total === 0) {
    return (
      <div className="flex flex-col justify-center my-6">
        <h2 className="text-center font-semibold text-2xl pt-6 p-4">Inga fakturor</h2>
      </div>
    );
  }

  const resources = fortnoxList.resources.map(resource => {
    if (resource && typeof resource.externalData === "string") {
      try {
        return {
          ...resource,
          externalData: JSON.parse(resource.externalData)
        };
      } catch (e) {
        console.error("Failed to parse externalData:", e);
        return {
          ...resource,
          externalData: {}
        };
      }
    }
    return resource;
  });

  const reset = (e) => {
    e.stopPropagation(); // Prevent row click navigation
    setAddedRows(new Set());
    setIgnoredRows(new Set());
  };

  const statusTranslations = {
    0: "Hämtad",
    1: "Köad för inläsning",
    2: "Inläst för inkasso",
    3: "Misslyckad inläsning",
    4: "Avvaktad",
  };

  return (
    <div className="-mx-4 mt-10 ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg">
      <table className="min-w-full divide-y divide-gray-300">
        <thead>
          <tr>
            <th className="py-3.5 pl-2 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-4">
              Fakturanr
            </th>
            <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
              Kund
            </th>
            <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
              Förfallodatum
            </th>
            <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
              Saldo
            </th>
            <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
              Senast hämtad
            </th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              Status
            </th>
            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              
            </th>
            {/* <th className=""><button className="w-32 px-2.5 py-1.5 text-sm ring-1 ring-inset rounded-md bg-white ring-blue-1 text-black rounded">Skicka vidare</button></th>
           <th><button className="w-32 px-2.5 text-sm py-1.5 ring-1 ring-inset rounded-md bg-white  ring-blue-1  text-black rounded">Pausa</button></th> */}
                       <th className="hidden lg:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
             Åtgärder
            </th>
            <th className="hidden lg:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
              
            </th>
           <th className="px-3 py-3.5 text-right text-sm">
                      <button onClick={(e) => reset(e)} className="border-b-2 border-white hover:border-blue-1 hover:border-b-2 font-semibold text-blue-1">
                X Avmarkera alla åtgärder
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {resources.map((resource, planIdx) => {
            if (!resource || !resource.externalData) return null;

            const rowClass = addedRows.has(resource.id)
              ? "bg-blue-2/10"
              : ignoredRows.has(resource.id)
              ? "bg-orange/30"
              : "";
            
            const isClickable = resource.status === 2;
            const rowStyles = isClickable 
              ? `${rowClass} cursor-pointer hover:bg-gray-50` 
              : rowClass;

            return (
              <tr 
                key={resource.externalData.DocumentNumber || planIdx} 
                className={rowStyles}
                onClick={() => handleRowClick(resource.vizionResourceId, resource.status)}
              >
                <td
                  className={classNames(
                    planIdx === 0 ? "" : "border-t border-gray-200",
                    "px-3 py-3.5 text-sm text-gray-600 lg:table-cell"
                  )}
                >
                  {resource.externalData.DocumentNumber}
                </td>
                <td
                  className={classNames(
                    planIdx === 0 ? "" : "border-t border-gray-200",
                    "hidden px-3 py-3.5 text-sm text-gray-600 lg:table-cell"
                  )}
                >
                  {resource.externalData.CustomerName}
                </td>
                <td
                  className={classNames(
                    planIdx === 0 ? "" : "border-t border-gray-200",
                    "hidden px-3 py-3.5 text-sm text-gray-600 lg:table-cell"
                  )}
                >
                  <span>{new Date(resource.externalData.DueDate).toLocaleDateString("sv-SE")}</span>
                </td>
                <td
                  className={classNames(
                    planIdx === 0 ? "" : "border-t border-gray-200",
                    "hidden px-3 py-3.5 text-sm text-gray-600 lg:table-cell"
                  )}
                >
                  {resource.externalData.Balance}
                  </td>
                  <td
                  className={classNames(
                    planIdx === 0 ? "" : "border-t border-gray-200",
                    "hidden px-3 py-3.5 text-sm text-gray-600 lg:table-cell"
                  )}
                >
                  {new Date(resource.updated || resource.created).toLocaleDateString("sv-SE")}
                  </td>
                <td
                  className={classNames(
                    planIdx === 0 ? "" : "border-t border-gray-200",
                    "px-3 py-3.5 text-sm text-gray-600"
                  )}
                >
                 <span>
                    {statusTranslations[resource.status]}
                  </span>
                  {resource.status === 2 ? (
                    <></>
                  ) : (
                    resource.messages && resource.messages.length > 0 && (
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMessageId(openMessageId === resource.id ? null : resource.id);
                          }}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <InformationCircleIcon className="h-5 w-5 text-red-500 align-bottom" />
                        </button>
                        {openMessageId === resource.id && (
                          <div
                            ref={popupRef}
                            className="absolute left-[-200px] top-0 bg-white border border-gray-300 p-2 rounded shadow-md w-60 z-50"
                          >
                            {resource.messages.map((message, index) => (
                              <div key={index} className="border-b border-gray-200 pb-2 mb-2 last:border-0 last:pb-0 last:mb-0">
                                <p className="text-xs text-gray-600">
                                  {new Date(message.created).toLocaleDateString("sv-SE")}
                                </p>
                                <p className="text-sm">{message.message}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  )
                  }
                </td>
                <td className="hidden lg:table-cell"></td>

                  {/* Action buttons based on status */}
                  {resource.status === 0 && (
                    <>
                    <td className="px-3 py-2 text-sm text-gray-600">
                      <button
                        onClick={(e) => handleAdd(e, resource.id)}
                        className={`w-32 flex justify-center rounded-md px-2.5 py-1.5 text-sm font-semibold text-blue-1 shadow-sm ring-1 ring-inset ring-blue-1 hover:bg-neutral-500 hover:bg-opacity-5`}
                      >
                        {addedRows.has(resource.id)? <><CheckIcon aria-hidden="true" stroke-width="1.5" className="mr-1 h-5 w-5 stroke-black group-bg-blue-2/10 group-hover:stroke-blue-1 transition-all duration-100 ease-in-out" /> Inkasso</>: 'Inkasso' }
                      </button>
                      </td>
                    <td className="px-3 py-2 text-sm text-gray-600">
                      <button
                        onClick={(e) => handleIgnore(e, resource.id)}
className={`flex justify-center w-32 rounded-md px-2.5 py-1.5 text-sm font-semibold text-blue-1 shadow-sm ring-1 ring-inset ring-blue-1 hover:bg-neutral-500 hover:bg-opacity-5`}
                      >
                        {ignoredRows.has(resource.id)? <><CheckIcon aria-hidden="true" stroke-width="1.5" className="mr-1 h-5 w-5 stroke-black group-hover:stroke-blue-1 transition-all duration-100 ease-in-out" /> Avvakta </> : 'Avvakta'}
                      </button>
                    </td>
                    </>
                  )}
                  {resource.status === 3 && (
                    <td className="px-3 py-2 text-sm text-gray-600">
                    <button
                      onClick={(e) => handleAdd(e, resource.id)}
                      className="w-32 flex justify-center rounded-md px-2.5 py-1.5 text-sm font-semibold text-blue-1 shadow-sm ring-1 ring-inset ring-blue-1 hover:bg-neutral-500 hover:bg-opacity-5"
                    >
                      Försök igen
                    </button>
                    </td>
                  )}
                  {resource.status === 4 && (
                    <td className="px-3 py-2 text-sm text-gray-600">
                      <button
                      onClick={(e) => handleAdd(e, resource.id)}
                      className={`w-32 flex justify-center rounded-md ${addedRows.has(resource.id)? '' : 'bg-white'} px-2.5 py-1.5 text-sm font-semibold text-blue-1 shadow-sm ring-1 ring-inset ring-blue-1 hover:bg-neutral-500 hover:bg-opacity-5`}
                    >
                      {addedRows.has(resource.id)? <><CheckIcon aria-hidden="true" stroke-width="1.5" className="mr-1 h-5 w-5 stroke-black group-bg-blue-2/10 group-hover:stroke-blue-1 transition-all duration-100 ease-in-out" /> Skicka</>: 'Skicka' }
                    </button>
                    </td>
                  )}
              <td></td>
              <td></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
